import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { QuestionnaireInstanceService } from '../../../api/services/questionnaire-instance.service';
import { Observable, of } from 'rxjs';
import { catchError, tap, timeout } from 'rxjs/operators';
import { QuestionModel } from '../../../api/models/question-model';
import { QuestionsService } from '../../../services/questions.service';
import { AuthService } from '../../../services/auth.service';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-welcome',
  templateUrl: './welcome.component.html',
  standalone: false,
})
export class WelcomeComponent implements OnInit {
  name: string;
  readonly starting = signal(false);
  private readonly startRequestTimeoutMs = 10000;
  private readonly activeQuestionnaireInstanceGuidStorageKey =
    'activeQuestionnaireInstanceGuid';
  private readonly respondentNameStorageKey = 'activeQuestionnaireRespondentName';
  private readonly navigationQuestionnaireInstanceGuid?: string;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private questionService: QuestionsService,
    private questionnaireInstanceService: QuestionnaireInstanceService,
    private authService: AuthService,
    private toastService: ToastService,
  ) {
    const navigation = this.router.getCurrentNavigation();
    const navigationState = navigation?.extras.state;
    this.navigationQuestionnaireInstanceGuid =
      navigationState?.['activeQuestionnaireInstanceGuid'];
    this.name =
      navigationState?.['name'] ||
      sessionStorage.getItem(this.respondentNameStorageKey) ||
      '';
  }

  ngOnInit() {
    console.info('Welcome Component initialised');
    this.resetScrollPosition();
    this.restoreQuestionnaireInstanceState();

    // We should log user out upon opening welcome page, so that user completing questionnaire cannot
    // use back button to access admin-only pages
    this.authService.logout();
  }

  /*
    Start the questionnaire, getting the first question, and redirecting to the question view
  */
  start() {
    if (this.starting()) return;

    const guid = this.getActiveQuestionnaireInstanceGuid();
    if (!guid) {
      console.error('QuestionnaireInstanceGuid is missing');
      this.toastService.error(
        'Could not start questionnaire. Please try again.',
      );
      return;
    }

    this.starting.set(true);
    // Prevent duplicate user-facing error handling between catchError and the
    // null-value subscribe branch.
    let startFailed = false;

    const question$: Observable<QuestionModel> =
      this.questionnaireInstanceService.startPost$Json({
        questionnaireInstanceGuid: guid,
      });

    question$
      .pipe(
        // Fail fast after 10s so users are not stuck on a spinner indefinitely.
        // Errors/timeouts are converted to `null` to keep subscribe logic centralized.
        timeout(this.startRequestTimeoutMs),
        tap(() => console.info('API call to start questionnaire successful')),
        catchError((err) => {
          console.error('Error starting questionnaire:', err);
          this.toastService.error(
            'Could not start questionnaire. Please try again.',
          );
          startFailed = true;
          return of(null);
        }),
      )
      .subscribe((firstQuestion) => {
        if (firstQuestion) {
          this.questionService.setCurrentQuestion(firstQuestion);
          this.router
            .navigate(['questions'], {
              state: { activeQuestionnaireInstanceGuid: guid },
            })
            .then((success) => {
              if (success) {
                console.info('Navigation to /questions successful');
              } else {
                console.error('Navigation to /questions failed');
                this.starting.set(false);
              }
            })
            .catch((err) => {
              console.error('Navigation to /questions threw an error:', err);
              this.starting.set(false);
            });
        } else {
          if (!startFailed) {
            console.error('No first question returned from API');
            this.toastService.error(
              'Could not start questionnaire. Please try again.',
            );
          }
          this.starting.set(false);
        }
      });
  }

  private resetScrollPosition(): void {
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
  }

  private restoreQuestionnaireInstanceState(): void {
    const guid = this.getActiveQuestionnaireInstanceGuid();
    if (guid) {
      this.questionnaireInstanceService.questionnaireInstanceGuid = guid;
      sessionStorage.setItem(
        this.activeQuestionnaireInstanceGuidStorageKey,
        guid,
      );
    }

    if (this.name) {
      sessionStorage.setItem(this.respondentNameStorageKey, this.name);
    }
  }

  private getActiveQuestionnaireInstanceGuid(): string | null {
    return (
      this.navigationQuestionnaireInstanceGuid ??
      this.route.snapshot.queryParamMap.get('questionnaireInstanceGuid') ??
      this.questionnaireInstanceService.questionnaireInstanceGuid ??
      sessionStorage.getItem(this.activeQuestionnaireInstanceGuidStorageKey)
    );
  }
}
